/**
 * SPA shape → database row.
 *
 * PORTED FROM `tradecoach-saas/app/index.html` `_tradeToDb` — semantics, not
 * code (D3), with one structural change that is the point of the rebuild.
 *
 * ## The change: exhaustiveness is enforced, not remembered
 *
 * The old serializer is a hand-written object literal. Adding a field to a trade
 * and forgetting to add it here drops the value on save, silently — which is
 * exactly how `consistencyScope` and `dllEnforcement` vanished on the accounts
 * side, and how the container trap in the type-surface audit stays live.
 *
 * Here the mapping is a `Record<keyof Trade, …>`: **a new field on `Trade`
 * without an entry is a compile error**, and `types.ts`'s `TRADE_COLUMNS` is the
 * single source of truth for the column name.
 */
import { TRADE_COLUMNS, type Trade } from '../../domain/types';
import { PROJECTION_TAINT, isProjectionTainted, type HydrationLedger } from './hydration';

/** A row as PostgREST accepts it — snake_case keys, JSON-safe values. */
export type DbRow = Record<string, unknown>;

/**
 * Keys that are persisted AS COLUMNS. Everything else on the object rides
 * `extras`, which round-trips (it is spread back on load) and is the
 * forward-compat seam: a new field persists with no migration.
 *
 * ⚠ **Only fields with a real column belong here.** A field listed as "known"
 * but written to no column is persisted NOWHERE — it is excluded from `extras`
 * precisely because it was claimed to have a home, and then does not get one.
 * That is the container trap from the type-surface audit, and the first draft of
 * this file walked straight into it with `tags` (which has `col: null`).
 *
 * Derived from `TRADE_COLUMNS` rather than hand-listed, so it cannot drift —
 * filtering out the null-column entries is what makes that derivation correct.
 */
export const TRADE_KNOWN_KEYS: ReadonlySet<string> = new Set(
  Object.entries(TRADE_COLUMNS)
    .filter(([, col]) => col != null)
    .map(([k]) => k),
);

/**
 * Everything on `obj` that is not a known key.
 *
 * ⚠ Shallow by design. A NESTED container listed as known (the old client's
 * `rules`, `core`, `tagging`) hides its own sub-keys from this rescue — the
 * container trap. The rebuild avoids it by having no nested containers: the
 * type is flat because the schema is flat.
 */
export function extractExtras(
  obj: Record<string, unknown>,
  knownKeys: ReadonlySet<string>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(obj ?? {})) if (!knownKeys.has(k)) out[k] = obj[k];
  return out;
}

// ---------------------------------------------------------------------------
// Value transforms
// ---------------------------------------------------------------------------
// The old serializer applies THREE different numeric treatments, and the
// difference is meaningful rather than accidental:
//
//   num0()    — absent becomes 0.   Used where 0 is the true default (qty, fees).
//   numNull() — absent stays null.  Used for MAE/MFE and rrRealized, where 0 and
//               "not measured" are different facts. Collapsing them would make
//               an unmeasured excursion look like a zero one and drag every
//               average toward zero.
//
// Reproduced exactly. Choosing the wrong one per field is a silent data change.

const num0 = (v: unknown): number => Number(v) || 0;
const numNull = (v: unknown): number | null => (v != null ? Number(v) : null);
const str = (v: unknown): string | null => (v as string) || null;

/**
 * The complete field mapping. Typed `Record<keyof Trade, …>` so it must cover
 * every field on `Trade` — that is the whole mechanism.
 *
 * `null` column means "not persisted directly"; the value rides `extras`.
 */
type FieldSpec = { col: string | null; to: (t: Trade) => unknown };

const TRADE_FIELDS: { [K in keyof Trade]-?: FieldSpec } = {
  id: { col: 'id', to: (t) => t.id },
  accountId: { col: 'account_id', to: (t) => t.accountId || null },
  date: { col: 'date', to: (t) => str(t.date) },
  sessionDate: { col: 'session_date', to: (t) => str(t.sessionDate) },
  timeIn: { col: 'time_in', to: (t) => str(t.timeIn) },
  timeOut: { col: 'time_out', to: (t) => str(t.timeOut) },
  contract: { col: 'contract', to: (t) => str(t.contract) },
  contractFull: { col: 'contract_full', to: (t) => str(t.contractFull) },
  side: { col: 'side', to: (t) => str(t.side) },
  qty: { col: 'qty', to: (t) => num0(t.qty) },
  entry: { col: 'entry', to: (t) => num0(t.entry) },
  exit: { col: 'exit', to: (t) => num0(t.exit) },
  points: { col: 'points', to: (t) => num0(t.points) },
  grossPnl: { col: 'gross_pnl', to: (t) => num0(t.grossPnl) },
  fees: { col: 'fees', to: (t) => num0(t.fees) },
  feesAuto: { col: 'fees_auto', to: (t) => !!t.feesAuto },
  netPnl: { col: 'net_pnl', to: (t) => num0(t.netPnl) },
  // Null-preserving: "not measured" ≠ "zero excursion".
  maePoints: { col: 'mae_points', to: (t) => numNull(t.maePoints) },
  mfePoints: { col: 'mfe_points', to: (t) => numNull(t.mfePoints) },
  maeSecs: { col: 'mae_secs', to: (t) => numNull(t.maeSecs) },
  mfeSecs: { col: 'mfe_secs', to: (t) => numNull(t.mfeSecs) },
  riskPoints: { col: 'risk_points', to: (t) => num0(t.riskPoints) },
  rrPlanned: { col: 'rr_planned', to: (t) => num0(t.rrPlanned) },
  // Null-preserving: a stop-less trade has no realised R, which is not 0.
  rrRealized: { col: 'rr_realized', to: (t) => numNull(t.rrRealized) },
  plannedStop: { col: 'planned_stop', to: (t) => num0(t.plannedStop) },
  plannedTarget: { col: 'planned_target', to: (t) => num0(t.plannedTarget) },
  riskAtEntry: { col: 'risk_at_entry', to: (t) => num0(t.riskAtEntry) },
  patternId: { col: 'pattern_id', to: (t) => t.patternId || null },
  exitModelId: { col: 'exit_model_id', to: (t) => t.exitModelId || null },
  grade: { col: 'grade', to: (t) => str(t.grade) },
  sessionLabel: { col: 'session_label', to: (t) => str(t.sessionLabel) },
  marketRegime: { col: 'market_regime', to: (t) => str(t.marketRegime) },
  newsEvent: { col: 'news_event', to: (t) => str(t.newsEvent) },
  mood: { col: 'mood', to: (t) => str(t.mood) },
  mistakeTags: { col: 'mistake_tags', to: (t) => t.mistakeTags ?? [] },
  // ⚠ The column is JSONB defaulting to '{}' (an object), and confluences is a
  // MAP of id → {active, notes}. The old client wrote `|| []` here — an array
  // fallback for an object-shaped column. Harmless (both are valid JSON) but
  // inconsistent; the empty OBJECT is written instead, matching the column
  // default and the type.
  confluences: { col: 'confluences', to: (t) => t.confluences ?? {} },
  booleans: { col: 'booleans', to: (t) => t.booleans ?? {} },
  controllableLossAmount: { col: 'controllable_loss_amount', to: (t) => num0(t.controllableLossAmount) },
  screenshots: { col: 'screenshots', to: (t) => t.screenshots ?? [] },
  recordings: { col: 'recordings', to: (t) => t.recordings ?? [] },
  notes: { col: 'notes', to: (t) => str(t.notes) },
  annotated: { col: 'annotated', to: (t) => !!t.annotated },
  imported: { col: 'imported', to: (t) => !!t.imported },
  sourceAccount: { col: 'source_account', to: (t) => str(t.sourceAccount) },
  syntheticClose: { col: 'synthetic_close', to: (t) => !!t.syntheticClose },
  flagged: { col: 'flagged', to: (t) => !!t.flagged },
  flagReasons: { col: 'flag_reasons', to: (t) => t.flagReasons ?? [] },
  // No column — rides extras. A per-user tag registry cannot be a column.
  tags: { col: null, to: (t) => t.tags ?? [] },
};

/**
 * A trade → its database row.
 *
 * `user_id` is passed rather than read from a global: `io/` should not know how
 * the session is stored, and it makes the function testable without auth.
 */
export function tradeToDb(t: Trade, userId: string, ledger: HydrationLedger): DbRow {
  // ⚠ THE GUARD, and the reason it is keyed on the id rather than on the keys.
  //
  // Every branch below writes its column unconditionally, so this function
  // cannot express "leave that one alone". A trade assembled from a narrow row —
  // `{ ...BLANK, ...listRow }`, which is how anyone builds an edit form — has
  // all 48 keys and 37 manufactured defaults, and would overwrite the user's
  // notes, screenshots, tags, mistake tags, discipline booleans and `extras`
  // with them. No database constraint stops it: every at-risk column is
  // nullable or defaulted, and `annotated`/`fees_auto` default TRUE so they
  // invert rather than merely blank.
  //
  // A key-presence check passes that object. An id cannot be forged by a
  // defaults spread, so membership is the only test that actually holds.
  // ⚠ CHECKED FIRST, and it is the guard that actually holds.
  //
  // The ledger below proves an id was fully read AT SOME POINT. It cannot prove
  // THIS OBJECT is that read — and a projected row carries the row's REAL id, so
  // once a trade has been opened, `{ ...BLANK, ...narrowRow }` for that same
  // trade sails through it. Reproduced, then fixed here.
  //
  // The taint travels with the data instead: spread, Object.assign,
  // structuredClone and JSON all copy an enumerable own key, so anything
  // assembled from a projection still carries it. Rejected BEFORE extractExtras
  // runs, so the marker can never be persisted.
  if (isProjectionTainted(t)) {
    throw new Error(
      `tradeToDb: refusing to serialize trade ${String(t.id)} — this object was built from ` +
        `a column PROJECTION (it still carries "${PROJECTION_TAINT}"), so most of its fields ` +
        `are defaults rather than the user's data. Saving it would overwrite notes, ` +
        `screenshots, tags, mistake tags, discipline booleans, MAE/MFE and extras. Hydrate ` +
        `the trade with loadFullTrades() and edit THAT object instead of spreading a ` +
        `projected row over a blank one.`,
    );
  }
  if (!ledger.isComplete(t.id)) {
    throw new Error(
      `tradeToDb: refusing to serialize trade ${String(t.id)} — it is not in the hydration ` +
        `ledger, so it may have been loaded with a narrow column projection. All ` +
        `${TRADE_WRITTEN_COLUMNS.length} columns are written on every save, so saving a ` +
        `partially loaded trade destroys every column it did not carry (notes, screenshots, ` +
        `tags, mistake tags, booleans, extras). Load it in full first (markHydrated), or if ` +
        `it is genuinely new, markNew(id) at the point it is created.`,
    );
  }
  const row: DbRow = { user_id: userId };
  for (const spec of Object.values(TRADE_FIELDS) as FieldSpec[]) {
    if (spec.col) row[spec.col] = spec.to(t);
  }
  row['extras'] = extractExtras(t as unknown as Record<string, unknown>, TRADE_KNOWN_KEYS);
  return row;
}

// ---------------------------------------------------------------------------
// Database row → SPA shape
// ---------------------------------------------------------------------------

const n0 = (v: unknown): number => Number(v) || 0;
const nNull = (v: unknown): number | null => (v != null ? Number(v) : null);

/**
 * A database row → a `Trade`.
 *
 * ⚠ `extras` is spread LAST, so a key in `extras` OVERRIDES the column-derived
 * value of the same name. That is the old client's behaviour and it is what
 * makes the forward-compat seam work: a field that later gains a real column
 * keeps reading from `extras` until the backfill runs, rather than flipping to
 * an empty column mid-migration.
 *
 * It is also a foot-gun — a stale `extras` key silently shadows a live column —
 * which is precisely why `TRADE_KNOWN_KEYS` excludes every column-bearing field
 * from `extras` on the way out. The two halves have to agree.
 */
/**
 * Every column `tradeFromDb` reads. A row missing any of them is NOT a trade —
 * it is a projection, and it must not be laundered into one.
 *
 * `extras` is included: absent, the whole forward-compat seam goes quiet and
 * `tags` silently reads `[]` for every trade.
 */
const FULL_TRADE_ROW_COLUMNS: readonly string[] = [
  ...(Object.values(TRADE_FIELDS) as FieldSpec[]).map((s) => s.col).filter((c) => !!c),
  'extras',
] as string[];

/**
 * Refuse a partially-selected row.
 *
 * The check is OWN-PROPERTY, not null — because null is a legal, meaningful
 * value for most of these columns, and the entire hazard is that this function
 * cannot otherwise tell the two apart. `r['notes']` is `undefined` both when the
 * user wrote no note and when the SELECT never asked for one, and the line
 * below turns both into `''`.
 *
 * The `id` field already fails loudly-ish downstream (`diffCollection` throws
 * when no row has one). This extends that doctrine to the other 47, where it
 * was missing.
 */
export function assertFullTradeRow(r: DbRow): void {
  const absent = FULL_TRADE_ROW_COLUMNS.filter(
    (c) => !Object.prototype.hasOwnProperty.call(r, c),
  );
  if (absent.length) {
    throw new Error(
      `tradeFromDb: row ${String(r['id'] ?? '(no id)')} is missing ${absent.length} column(s): ` +
        `${absent.join(', ')}. An absent column and a NULL column are indistinguishable here — ` +
        `both would become 0 / '' / false / [] — and every column is written back on save, so ` +
        `this row would overwrite real data with those defaults. Read it with select('*').`,
    );
  }
}

export function tradeFromDb(r: DbRow): Trade {
  assertFullTradeRow(r);
  const base = {
    id: r['id'],
    accountId: r['account_id'] ?? null,
    date: r['date'] ?? null,
    sessionDate: r['session_date'] ?? null,
    timeIn: r['time_in'] ?? null,
    timeOut: r['time_out'] ?? null,
    contract: r['contract'] ?? null,
    contractFull: r['contract_full'] ?? null,
    side: r['side'] ?? null,
    qty: n0(r['qty']),
    entry: n0(r['entry']),
    exit: n0(r['exit']),
    points: n0(r['points']),
    grossPnl: n0(r['gross_pnl']),
    fees: n0(r['fees']),
    feesAuto: !!r['fees_auto'],
    netPnl: n0(r['net_pnl']),
    maePoints: nNull(r['mae_points']),
    mfePoints: nNull(r['mfe_points']),
    maeSecs: nNull(r['mae_secs']),
    mfeSecs: nNull(r['mfe_secs']),
    riskPoints: n0(r['risk_points']),
    rrPlanned: n0(r['rr_planned']),
    rrRealized: nNull(r['rr_realized']),
    plannedStop: n0(r['planned_stop']),
    plannedTarget: n0(r['planned_target']),
    riskAtEntry: n0(r['risk_at_entry']),
    patternId: r['pattern_id'] ?? null,
    exitModelId: r['exit_model_id'] ?? null,
    grade: r['grade'] ?? '',
    sessionLabel: r['session_label'] ?? '',
    marketRegime: r['market_regime'] ?? '',
    newsEvent: r['news_event'] ?? '',
    mood: r['mood'] ?? '',
    mistakeTags: r['mistake_tags'] ?? [],
    tags: [],
    confluences: r['confluences'] ?? {},
    booleans: r['booleans'] ?? {},
    controllableLossAmount: n0(r['controllable_loss_amount']),
    screenshots: r['screenshots'] ?? [],
    recordings: r['recordings'] ?? [],
    notes: r['notes'] ?? '',
    annotated: !!r['annotated'],
    imported: !!r['imported'],
    sourceAccount: r['source_account'] ?? null,
    syntheticClose: !!r['synthetic_close'],
    flagged: !!r['flagged'],
    flagReasons: r['flag_reasons'] ?? [],
  };
  return { ...base, ...((r['extras'] as Record<string, unknown>) ?? {}) } as unknown as Trade;
}

/** Column names this serializer writes, in a stable order. For tests and drift checks. */
export const TRADE_WRITTEN_COLUMNS: readonly string[] = [
  'user_id',
  ...(Object.values(TRADE_FIELDS) as FieldSpec[]).map((s) => s.col).filter((c): c is string => !!c),
  'extras',
];
